import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsResponse } from '@cloudsign/shared';
import type { z } from 'zod';
import { api } from './api';
import { diffValues } from './settingsSchema';

/**
 * Loads one module's settings for a site or group, keeps an editable copy typed by the shared schema, and saves only what changed.
 * The query key matches SettingsEditor's, so both see each other's saves.
 */
export function useSettingsDraft<S extends z.ZodType>(base: string, moduleKey: string, schema: S) {
  type T = z.output<S>;
  const qc = useQueryClient();
  const key = ['settings', base, moduleKey];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`${base}/${moduleKey}`, { schema: SettingsResponse }) });
  const [draft, setDraft] = useState<T | null>(null);
  const original: T | null = q.data ? (schema.parse(q.data.values) as T) : null;
  useEffect(() => {
    if (q.data) setDraft(schema.parse(q.data.values) as T);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => api.patch(`${base}/${moduleKey}`, { body: diffValues(original, draft), schema: SettingsResponse }),
    onSuccess: (r) => {
      qc.setQueryData(key, r);
      setDraft(schema.parse(r.values) as T);
    },
  });
  const changed = original !== null && draft !== null && Object.keys(diffValues(original, draft)).length > 0;
  return { q, draft, setDraft, original, changed, save, valid: draft !== null && schema.safeParse(draft).success };
}
